import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface AuditEvent {
  id: string
  timestamp: string
  user: string
  action: string
  resource: string
  status: string
  ip: string
}

interface EventCategory {
  category: string
  events_today: number
  events_mtd: number
  trend: string
  change: number
}

interface Statistics {
  events_today: number
  events_mtd: number
  unique_users: number
  data_access_events: number
  change_events: number
  security_events: number
}

export default function AuditTrailPage() {
  const [events, setEvents] = useState<AuditEvent[]>([])
  const [categories, setCategories] = useState<EventCategory[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [eventsRes, categoriesRes, statsRes] = await Promise.all([
        fetch('/api/audittrail/events'),
        fetch('/api/audittrail/categories'),
        fetch('/api/audittrail/statistics')
      ])
      if (eventsRes.ok) setEvents(await eventsRes.json())
      if (categoriesRes.ok) setCategories(await categoriesRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching audit trail:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value)
  }

  const getStatusBadge = (status: string) => {
    if (status === 'Success') return 'bg-green-100 text-green-800'
    if (status === 'Failed') return 'bg-red-100 text-red-800'
    return 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Audit Trail</h1>
          <p className="mt-2 text-gray-600">Phase 269 - Activity logs and compliance audits</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Events Today</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(statistics.events_today)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Events MTD</p>
              <p className="text-2xl font-bold text-blue-600">{formatNumber(statistics.events_mtd)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Unique Users</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.unique_users}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Data Access</p>
              <p className="text-2xl font-bold text-green-600">{formatNumber(statistics.data_access_events)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Change Events</p>
              <p className="text-2xl font-bold text-orange-600">{formatNumber(statistics.change_events)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Security Events</p>
              <p className="text-2xl font-bold text-red-600">{statistics.security_events}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Event Categories</h2>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((cat, idx) => (
              <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{cat.category}</span>
                  <span className={`text-sm font-bold ${cat.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>{cat.change >= 0 ? '+' : ''}{cat.change}%</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Today</p>
                    <p className="font-medium text-blue-600">{formatNumber(cat.events_today)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">MTD</p>
                    <p className="font-medium text-purple-600">{formatNumber(cat.events_mtd)}</p>
                  </div>
                </div>
                <p className={`mt-2 text-xs ${cat.trend === 'up' ? 'text-green-600' : cat.trend === 'down' ? 'text-red-600' : 'text-gray-500'}`}>Trend: {cat.trend}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Recent Events</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Resource</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {events.map((event) => (
                  <tr key={event.id}>
                    <td className="px-6 py-4 text-gray-500 text-sm">{event.timestamp}</td>
                    <td className="px-6 py-4 font-medium text-gray-900">{event.user}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">{event.action}</span>
                    </td>
                    <td className="px-6 py-4 text-purple-600">{event.resource}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(event.status)}`}>{event.status}</span>
                    </td>
                    <td className="px-6 py-4 text-gray-500 font-mono text-sm">{event.ip}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  )
}
